<template>
  <form class="mic-button">
    <div>
      <div>마이크 음성 테스트</div>
      <div>마이크 on/off: {{ microphoneStore.isMicOn }}</div>
      <div>등록로딩: {{ uploading }}</div>
    </div>
    <button
      v-if="!microphoneStore.isMicOn"
      type="button"
      class="btn"
      @click="microphoneStore.startMic(microphoneStore.selectedMicrophone.id)"
    >
      음성등록 시작
    </button>
    <button v-else type="button" class="btn" @click="registerMyVoice">
      음성등록 중지
    </button>
  </form>
  <ul>
    <li
      v-for="(microphone, microphoneIndex) in microphoneStore.microphones"
      :key="`microphone_${microphoneIndex}`"
    >
      {{ microphone.label }}:
      <button
        type="button"
        @click="microphoneStore.startMic(microphone.id)"
        :class="[
          'btn',
          microphone.id !== microphoneStore.selectedMicrophone?.id
            ? 'btn-outline-primary'
            : '',
        ]"
      >
        {{ microphone }}
      </button>
    </li>
  </ul>
  <hr />
  <div>내 마이크 감도조절:</div>
  <input
    v-model="microphoneStore.micSensitivity"
    type="range"
    min="0"
    max="10"
    :step="1"
    @change="microphoneStore.controlMicGain"
  />
  <hr />
  <div>내 마이크 데시벨:</div>
  <div>{{ microphoneStore.outerRadius }}</div>
  <hr />
  <div class="conference-group">
    <span class="button-label">🎙 3 Options Test</span>
    <div class="button-group column">
      <button class="btn" @click="echoTest">
        <span
          >echoCancellation - {{ constraints?.audio?.echoCancellation }}</span
        >
      </button>
      <button class="btn" @click="noiseTest">
        <span
          >noiseSuppression - {{ constraints?.audio?.noiseSuppression }}</span
        >
      </button>
      <button class="btn" @click="gainTest">
        <span>autoGainControl - {{ constraints?.audio?.autoGainControl }}</span>
      </button>
    </div>
  </div>
  <hr />
  <div class="conference-group">
    <span class="button-label">🎙initialGain</span>
    <input
      v-if="micInx !== 1 && microphones[micInx]"
      v-model="microphones[micInx].initialGain"
      type="number"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

import { isObject } from 'lodash';
import { storeToRefs } from 'pinia';
import { WaveFile } from 'wavefile';

import api from '@/api';
import { useMicrophoneStore } from '@/stores/microphone';
import { SAMPLE_RATE } from '@/utils/constants/microphone';

const microphoneStore = useMicrophoneStore();
const { constraints, microphones } = storeToRefs(microphoneStore);

const voiceRecordingResult = ref(null);
const audioBufferMap = ref([]);
const uploading = ref(false);

const micInx = computed<number>(() => {
  if (constraints.value) {
    const { deviceId: { exact = '' } = { exact: '' } } =
      constraints.value.audio;
    return microphones.value.findIndex(({ id }) => exact === id);
  }
  return -1;
});

const registerMyVoice = async () => {
  uploading.value = true;
  await microphoneStore.clearMic();

  try {
    await handleVoiceRecordingResult();

    const formData = new FormData();
    formData.append('voice', voiceRecordingResult.value);

    await api.post('/api/chat/business/call/voice/test', formData);
  } catch (err) {
    console.log(err);
  } finally {
    voiceRecordingResult.value = null;
    uploading.value = false;
  }
};

const handleVoiceRecordingResult = async () => {
  const wav = new WaveFile();
  wav.fromScratch(1, SAMPLE_RATE, '32f', [...audioBufferMap.value]);

  const wavBuffer = wav.toBuffer();
  voiceRecordingResult.value = new Blob([wavBuffer], { type: 'audio/wav' });
  audioBufferMap.value = [];
};

const echoTest = () => {
  if (constraints.value && isObject(constraints.value.audio)) {
    constraints.value.audio = {
      ...constraints.value.audio,
      echoCancellation: !constraints.value.audio.echoCancellation,
    };
    console.log(
      'constraints-echoCancellation',
      constraints.value.audio.echoCancellation
    );
  }
};

const noiseTest = () => {
  if (constraints.value && isObject(constraints.value.audio)) {
    constraints.value.audio = {
      ...constraints.value.audio,
      noiseSuppression: !constraints.value.audio.noiseSuppression,
    };
    console.log(
      'constraints-noiseSuppression',
      constraints.value.audio.noiseSuppression
    );
  }
};

const gainTest = () => {
  if (constraints.value && isObject(constraints.value.audio)) {
    constraints.value.audio = {
      ...constraints.value.audio,
      autoGainControl: !constraints.value.audio.autoGainControl,
    };
    console.log(
      'constraints-autoGainControl',
      constraints.value.audio.autoGainControl
    );
  }
};

onMounted(async () => {
  await microphoneStore.initMicrophoneStore({
    isUsingMicWithVoice: false,
    isUsingDeviceAutoUpdate: false,
    isUsingSpecificGain: true,
  });
  microphoneStore.initMicEvent((data: MessageEvent<Float32Array>) => {
    const scaled = data.map((v) => v * 3);
    audioBufferMap.value.push(...scaled);
    // audioBufferMap.value.push(...data);
  });
});
onUnmounted(() => {
  microphoneStore.clearMic();
});
</script>

<style scoped>
.mic-button {
  display: flex;
  justify-content: right;
}
</style>
